import { Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { ControlWrapper } from "./ControlWrapper";

const objectPath = require("object-path");

const toString = (value) => {
  return typeof value === "string" ? value : value?.toString();
};

export const ControlRemoteSelect = ({
  name,
  label,
  placeholder = "Escriba para buscar...",
  onChange,
  getOptions,
  initialItem,
  itemToOption = item => item,
  data,
  start,
  end,
  errors = [],
  isMulti = false,
  Wrapper = ControlWrapper,
  isClearable = true,
  type = "number",
  minLength = 1,
  debounce = 300,
  ...moreOptions
}) => {
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef(null);
  const requestRef = useRef(0);

  const initialOption = useMemo(() => {
    return initialItem ? itemToOption(initialItem) : null;
  }, [initialItem, itemToOption]);

  useEffect(() => {
    if (!initialOption) return;

    setOptions(prev => {
      const exists = prev.some(option => toString(option.value) === toString(initialOption.value));
      return exists ? prev : [initialOption, ...prev];
    });
  }, [initialOption]);

  const currentValue = objectPath(data).get(name, null);

  const value = useMemo(() => {
    if (!currentValue) {
      return isMulti ? [] : null;
    }

    const values = isMulti
      ? (currentValue?.map ? currentValue : currentValue.toString().split(","))
      : [currentValue];

    const availableOptions = [...selectedOptions, ...options];

    const result = values
      .map(current => availableOptions.find(option => toString(option.value) === toString(current)))
      .filter(Boolean);

    return isMulti ? result : result[0] || null;
  }, [currentValue, isMulti, options, selectedOptions]);

  const loadOptions = useCallback((term) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!term || term.trim().length < minLength) {
      requestRef.current += 1;
      setIsLoading(false);
      setOptions(initialOption ? [initialOption] : []);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      const requestId = ++requestRef.current;
      setIsLoading(true);

      try {
        const response = await getOptions(term.trim());

        if (requestId !== requestRef.current) return;

        const results = response?.results || [];
        setOptions(prev => {
          const selected = prev.filter(option =>
            selectedOptions.some(selected => toString(selected.value) === toString(option.value))
          );

          return [
            ...selected,
            ...results.filter(result =>
              !selected.some(option => toString(option.value) === toString(result.value))
            )
          ];
        });
      } finally {
        if (requestId === requestRef.current) {
          setIsLoading(false);
        }
      }
    }, debounce);
  }, [debounce, getOptions, initialOption, minLength, selectedOptions]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      requestRef.current += 1;
    };
  }, []);

  const handleChange = useCallback((selected) => {
    const normalized = selected?.map
      ? selected
      : (selected ? [selected] : []);

    setSelectedOptions(normalized);

    onChange({
      target: {
        name,
        value: selected?.map
          ? selected.map(option => type === "number" ? parseInt(option.value || 0) : option.value)
          : (type === "number" ? parseInt(selected?.value || 0) : selected?.value)
      }
    }, selected);
  }, [name, onChange, type]);

  return (
    <Wrapper name={name} errors={errors} end={end} start={start} label={label}>
      <Select
        onChange={handleChange}
        onInputChange={(value, action) => {
          if (action.action === "input-change") {
            loadOptions(value);
          }

          return value;
        }}
        closeMenuOnSelect={!isMulti}
        value={value}
        isMulti={isMulti}
        options={options}
        isLoading={isLoading}
        filterOption={null}
        className="flex-grow-1 h-100 w-100"
        isClearable={isClearable}
        placeholder={placeholder}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.trim().length < minLength
            ? `Escriba al menos ${minLength} caracter${minLength === 1 ? "" : "es"} para buscar`
            : "Sin resultados"
        }
        {...moreOptions}
        styles={{
          menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: 50000
          })
        }}
      />
    </Wrapper>
  );
};

const formatOptionLabel = ({ label }) => {
  return <Typography color="black">{label}</Typography>;
};
